/**
 * Category report (Phase 3, §6.3). Two views over the selected range:
 *  1. a single-month donut (the range's end month) — spend-by-bucket, centre
 *     hero = total Ausgaben, ranked BucketChip legend;
 *  2. a per-bucket spend trend over the range (stacked area or multi-line).
 *
 * Slice/series colour follows the entity (`bucket.color`) with the categorical
 * palette as a fixed-slot fallback; slot 9+ folds to "Andere" (muted grey).
 * Bucket metadata is joined from the single `['buckets']` cache.
 */
import { useMemo, useState } from 'react';
import { formatMonth, formatMonthShort } from '../../lib/format.ts';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SegmentedControl } from '../../components/ui/SegmentedControl.tsx';
import { Skeleton } from '../../components/ui/Skeleton.tsx';
import { useBucketMap } from '../../lib/hooks/useBuckets.ts';
import { useReportBucketTrend, useReportCategories } from '../../lib/hooks/useReports.ts';
import type { MonthRange } from '../../lib/queryKeys.ts';
import type { BucketOut } from '../../lib/types.ts';
import { Donut, type DonutSlice } from './charts/Donut.tsx';
import { StackedAreaChart, type AreaSeries } from './charts/StackedAreaChart.tsx';
import { LineChart, type LineSeries } from './charts/LineChart.tsx';
import { getBucketColor, OTHER_COLOR, type ChartMode } from './charts/palette.ts';
import { useChartMode } from './charts/useChartDims.tsx';
import { money } from '../../lib/format.ts';

const MAX_SLOTS = 8;

const toNum = (m: string): number => {
  const n = Number.parseFloat(m);
  return Number.isFinite(n) ? n : 0;
};

function bucketLabel(bucket: BucketOut | undefined, id: number): string {
  return bucket?.name ?? `Kategorie ${id}`;
}

type TrendView = 'flaeche' | 'linien';

export function CategoryReport({ range }: { range: MonthRange }) {
  const mode = useChartMode();
  const bucketMap = useBucketMap();
  const month = range.to;
  const [trendView, setTrendView] = useState<TrendView>('flaeche');

  const categories = useReportCategories(month);
  const trend = useReportBucketTrend(range);

  // The endpoint is a spend matrix (value = -actual), but the backend only
  // drops series whose range total is exactly 0 — income buckets (strongly
  // negative totals, e.g. Gehalt) still come through. The Ausgaben-Trend shows
  // net-outflow buckets only (matching the donut), so filter those out here
  // BEFORE the slot/„Andere“ fold; single refund months inside a net-outflow
  // bucket keep their honest negative cell values.
  const trendSeries = useMemo(
    () => (trend.data?.buckets ?? []).filter((s) => toNum(s.total) > 0),
    [trend.data],
  );

  return (
    <div className="space-y-10">
      <DonutSection month={month} data={categories} bucketMap={bucketMap} mode={mode} />

      <div>
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Ausgaben-Trend
          </h3>
          <SegmentedControl<TrendView>
            value={trendView}
            onChange={setTrendView}
            ariaLabel="Darstellung"
            options={[
              { value: 'flaeche', label: 'Fläche' },
              { value: 'linien', label: 'Linien' },
            ]}
          />
        </div>

        {trend.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : trend.isError ? (
          <ErrorState
            title="Trend konnte nicht geladen werden"
            error={trend.error}
            onRetry={trend.refetch}
          />
        ) : !trend.data || trendSeries.length === 0 ? (
          <EmptyState
            title="Keine Ausgaben im Zeitraum"
            description="Für diesen Zeitraum gibt es keine kategorisierten Ausgaben."
          />
        ) : (
          <TrendChart
            months={trend.data.months}
            series={trendSeries}
            bucketMap={bucketMap}
            mode={mode}
            view={trendView}
          />
        )}
      </div>
    </div>
  );
}

interface DonutSectionProps {
  month: string;
  data: ReturnType<typeof useReportCategories>;
  bucketMap: Map<number, BucketOut>;
  mode: ChartMode;
}

function DonutSection({ month, data, bucketMap, mode }: DonutSectionProps) {
  const { data: breakdown, isLoading, isError, error, refetch } = data;

  const slices = useMemo<DonutSlice[]>(() => {
    if (!breakdown) return [];
    const head = breakdown.slices.slice(0, MAX_SLOTS);
    const tail = breakdown.slices.slice(MAX_SLOTS);
    const out: DonutSlice[] = head.map((s, i) => {
      const bucket = bucketMap.get(s.bucket_id);
      return {
        key: String(s.bucket_id),
        label: bucketLabel(bucket, s.bucket_id),
        value: toNum(s.amount),
        color: getBucketColor(bucket, mode, i),
      };
    });
    if (tail.length > 0) {
      out.push({
        key: 'andere',
        label: 'Andere',
        value: tail.reduce((a, s) => a + toNum(s.amount), 0),
        color: OTHER_COLOR[mode],
      });
    }
    return out;
  }, [breakdown, bucketMap, mode]);

  return (
    <div>
      {isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : isError ? (
        <ErrorState
          title="Kategorien konnten nicht geladen werden"
          error={error}
          onRetry={refetch}
        />
      ) : !breakdown || slices.length === 0 ? (
        <EmptyState
          title="Keine Ausgaben"
          description={`Für ${formatMonth(month)} gibt es keine kategorisierten Ausgaben.`}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 md:items-center">
          <Donut
            title={`Ausgaben nach Kategorie — ${formatMonth(month)}`}
            subtitle="Einzelner Monat"
            slices={slices}
            centerLabel="Ausgaben gesamt"
            centerValue={money(breakdown.total)}
          />
          <ul className="space-y-1.5">
            {slices.map((slice) => {
              const total = toNum(breakdown.total);
              const pct = total > 0 ? (slice.value / total) * 100 : 0;
              return (
                <li key={slice.key} className="flex items-center gap-2 text-sm">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-400">
                    {slice.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-slate-400 dark:text-slate-500">
                    {pct.toFixed(1).replace('.', ',')} %
                  </span>
                  <span className="w-24 shrink-0 text-right tabular-nums text-slate-900 dark:text-slate-100">
                    {money(slice.value)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface TrendChartProps {
  months: string[];
  series: { bucket_id: number; total: string; values: string[] }[];
  bucketMap: Map<number, BucketOut>;
  mode: ChartMode;
  view: TrendView;
}

function TrendChart({ months, series, bucketMap, mode, view }: TrendChartProps) {
  const categories = months.map(formatMonthShort);

  // series are pre-sorted by total desc; fold slot 9+ into a single "Andere".
  const head = series.slice(0, MAX_SLOTS);
  const tail = series.slice(MAX_SLOTS);

  const built = head.map((s, i) => {
    const bucket = bucketMap.get(s.bucket_id);
    return {
      key: String(s.bucket_id),
      label: bucketLabel(bucket, s.bucket_id),
      color: getBucketColor(bucket, mode, i),
      values: s.values.map(toNum),
    };
  });
  if (tail.length > 0) {
    const folded = months.map((_, i) => tail.reduce((a, s) => a + toNum(s.values[i] ?? '0'), 0));
    built.push({ key: 'andere', label: 'Andere', color: OTHER_COLOR[mode], values: folded });
  }

  if (view === 'linien') {
    const lineSeries: LineSeries[] = built.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      values: s.values,
    }));
    return (
      <LineChart
        title="Ausgaben je Kategorie"
        subtitle="Verlauf über den Zeitraum"
        categories={categories}
        series={lineSeries}
      />
    );
  }

  const areaSeries: AreaSeries[] = built.map((s) => ({
    key: s.key,
    label: s.label,
    color: s.color,
    values: s.values,
  }));
  return (
    <StackedAreaChart
      title="Ausgaben je Kategorie"
      subtitle="Gestapelt über den Zeitraum"
      categories={categories}
      series={areaSeries}
    />
  );
}
